import { useState, useEffect } from 'react';
import { workflowAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  GripVertical,
  Layout,
  FileText,
  Video,
  Briefcase,
  Image,
  MousePointerClick,
  Heart,
  Settings2,
  Eye,
  EyeOff,
  Save,
  X,
  Download
} from 'lucide-react';

const STEP_TYPES = [
  { type: 'landing_page', label: 'Landing Page', icon: Layout, desc: 'Profile info, contact buttons', required: true },
  { type: 'lead_form', label: 'Lead Form', icon: FileText, desc: 'Capture visitor contact info' },
  { type: 'video', label: 'Video', icon: Video, desc: 'Show a profile video' },
  { type: 'services', label: 'Services', icon: Briefcase, desc: 'Display services list' },
  { type: 'photos', label: 'Photos', icon: Image, desc: 'Show photo gallery' },
  { type: 'pdf_download', label: 'PDF Download', icon: Download, desc: 'Brochure/pricelist download' },
  { type: 'cta', label: 'Call to Action', icon: MousePointerClick, desc: 'Button with link' },
  { type: 'thank_you', label: 'Thank You', icon: Heart, desc: 'Final message to visitor' },
];

const getStepIcon = (type) => {
  const found = STEP_TYPES.find(s => s.type === type);
  return found ? found.icon : Layout;
};

const getStepLabel = (type) => {
  const found = STEP_TYPES.find(s => s.type === type);
  return found ? found.label : type;
};

const WorkflowBuilder = ({ profileId, videos = [], pdfs = [] }) => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchWorkflow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const fetchWorkflow = async () => {
    try {
      const response = await workflowAPI.get(profileId);
      const wfSteps = response.data?.steps || [];
      if (wfSteps.length === 0) {
        // Default workflow
        setSteps([
          { id: `s_${Date.now()}`, type: 'landing_page', enabled: true, order: 1, settings: {} }
        ]);
      } else {
        setSteps(wfSteps);
      }
    } catch (error) {
      setSteps([
        { id: `s_${Date.now()}`, type: 'landing_page', enabled: true, order: 1, settings: {} }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddStep = (type) => {
    if (steps.length >= 8) {
      toast.error('Maximum 8 steps allowed');
      return;
    }
    const newStep = {
      id: `s_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
      type,
      enabled: true,
      order: steps.length + 1,
      settings: type === 'lead_form' ? { skippable: false, button_text: 'Submit' } :
                type === 'cta' ? { label: 'Learn More', url: '' } :
                type === 'thank_you' ? { message: 'Thank you for visiting!' } :
                type === 'pdf_download' ? { label: 'Download Pricelist', gating: 'none', pdf_ids: [] } :
                type === 'video' ? { video_index: 0 } : {}
    };
    setSteps([...steps, newStep]);
    setShowAddMenu(false);
    setHasChanges(true);
  };

  const handleRemoveStep = (stepId) => {
    const step = steps.find(s => s.id === stepId);
    if (step?.type === 'landing_page' && steps.filter(s => s.type === 'landing_page').length <= 1) {
      toast.error('Workflow must have a Landing Page step');
      return;
    }
    setSteps(steps.filter(s => s.id !== stepId).map((s, i) => ({ ...s, order: i + 1 })));
    setHasChanges(true);
  };

  const handleMoveStep = (index, direction) => {
    const newSteps = [...steps];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newSteps.length) return;
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    setSteps(newSteps.map((s, i) => ({ ...s, order: i + 1 })));
    setHasChanges(true);
  };

  const handleToggleStep = (stepId) => {
    setSteps(steps.map(s => s.id === stepId ? { ...s, enabled: !s.enabled } : s));
    setHasChanges(true);
  };

  const handleUpdateSettings = (stepId, settings) => {
    setSteps(steps.map(s => s.id === stepId ? { ...s, settings: { ...s.settings, ...settings } } : s));
    setHasChanges(true);
    setEditingStep(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await workflowAPI.update(profileId, { steps });
      toast.success('Workflow saved!');
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="workflow-builder">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Workflow Builder
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Configure what visitors see when they visit your profile
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="text-xs text-amber-600 font-medium">Unsaved changes</span>
          )}
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="save-workflow-btn"
          >
            {saving ? <div className="spinner w-4 h-4 border-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Workflow
          </Button>
        </div>
      </div>

      {/* Step List */}
      <div className="space-y-2">
        {steps.map((step, index) => {
          const StepIcon = getStepIcon(step.type);
          return (
            <div
              key={step.id}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                step.enabled ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'
              }`}
              data-testid={`workflow-step-${step.id}`}
            >
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => handleMoveStep(index, -1)}
                  disabled={index === 0}
                  className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                  data-testid={`move-up-${step.id}`}
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleMoveStep(index, 1)}
                  disabled={index === steps.length - 1}
                  className="p-0.5 text-slate-400 hover:text-slate-600 disabled:opacity-30"
                  data-testid={`move-down-${step.id}`}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>

              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                <StepIcon className="w-4 h-4 text-indigo-600" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 font-mono">#{index + 1}</span>
                  <span className="font-medium text-slate-900 text-sm">{getStepLabel(step.type)}</span>
                  {step.type === 'landing_page' && (
                    <span className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-600 rounded">Required</span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleToggleStep(step.id)}
                  className={`p-1.5 rounded-md transition-colors ${step.enabled ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:bg-slate-100'}`}
                  title={step.enabled ? 'Disable' : 'Enable'}
                >
                  {step.enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
                {['lead_form', 'video', 'cta', 'thank_you', 'pdf_download'].includes(step.type) && (
                  <button
                    onClick={() => setEditingStep(editingStep === step.id ? null : step.id)}
                    className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                    data-testid={`configure-${step.id}`}
                  >
                    <Settings2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleRemoveStep(step.id)}
                  className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
                  data-testid={`delete-step-${step.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Step Settings Panel */}
      {editingStep && (
        <StepSettingsPanel
          step={steps.find(s => s.id === editingStep)}
          videos={videos}
          pdfs={pdfs}
          onSave={(settings) => handleUpdateSettings(editingStep, settings)}
          onClose={() => setEditingStep(null)}
        />
      )}

      {/* Add Step */}
      <div className="relative">
        <Button
          variant="outline"
          className="w-full border-dashed"
          onClick={() => setShowAddMenu(!showAddMenu)}
          disabled={steps.length >= 8}
          data-testid="add-step-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Step {steps.length >= 8 && '(Max 8)'}
        </Button>

        {showAddMenu && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-10 p-2" data-testid="add-step-menu">
            {STEP_TYPES.map((stepType) => (
              <button
                key={stepType.type}
                onClick={() => handleAddStep(stepType.type)}
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors text-left"
                data-testid={`add-step-${stepType.type}`}
              >
                <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
                  <stepType.icon className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{stepType.label}</p>
                  <p className="text-xs text-slate-500">{stepType.desc}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Info */}
      <div className="bg-indigo-50 rounded-lg p-4 text-sm text-indigo-700">
        <p className="font-medium mb-1">How it works</p>
        <p>Visitors will see each enabled step in order. They progress through the flow from step 1 to the last step. The Landing Page step is always required.</p>
      </div>
    </div>
  );
};

const StepSettingsPanel = ({ step, videos, pdfs, onSave, onClose }) => {
  const [settings, setSettings] = useState(step?.settings || {});

  if (!step) return null;

  return (
    <div className="bg-slate-50 rounded-xl border border-slate-200 p-4" data-testid="step-settings-panel">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-slate-900">Configure: {getStepLabel(step.type)}</h4>
        <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-3">
        {step.type === 'lead_form' && (
          <>
            <div className="flex items-center justify-between">
              <Label>Allow Skip</Label>
              <button
                onClick={() => setSettings({ ...settings, skippable: !settings.skippable })}
                className={`w-10 h-6 rounded-full transition-colors ${settings.skippable ? 'bg-indigo-600' : 'bg-slate-300'}`}
                data-testid="skippable-toggle"
              >
                <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform mx-1 ${settings.skippable ? 'translate-x-4' : ''}`} />
              </button>
            </div>
            <div>
              <Label>Button Text</Label>
              <Input
                className="mt-1"
                value={settings.button_text || 'Submit'}
                onChange={(e) => setSettings({ ...settings, button_text: e.target.value })}
                placeholder="Submit"
                data-testid="lead-form-button-text"
              />
            </div>
          </>
        )}

        {step.type === 'video' && (
          <div>
            <Label>Select Video</Label>
            {videos.length > 0 ? (
              <div className="mt-1 space-y-2">
                {videos.map((v, i) => (
                  <button
                    key={v.id || i}
                    onClick={() => setSettings({ ...settings, video_index: i, video_id: v.id })}
                    className={`w-full text-left p-2 rounded-lg border transition-colors ${
                      (settings.video_index === i) ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <p className="text-sm font-medium">{v.title || `Video ${i + 1}`}</p>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 mt-1">No videos added to this profile yet</p>
            )}
          </div>
        )}

        {step.type === 'pdf_download' && (
          <>
            <div>
              <Label>Button Label</Label>
              <Input
                className="mt-1"
                value={settings.label || ''}
                onChange={(e) => setSettings({ ...settings, label: e.target.value })}
                placeholder="Download Pricelist"
                data-testid="pdf-label-input"
              />
            </div>
            <div>
              <Label>Select PDF(s)</Label>
              {pdfs.length > 0 ? (
                <div className="mt-1 space-y-2">
                  {pdfs.map((p) => {
                    const selected = (settings.pdf_ids || []).includes(p.id);
                    return (
                      <button
                        key={p.id}
                        onClick={() => {
                          const ids = settings.pdf_ids || [];
                          setSettings({ ...settings, pdf_ids: selected ? ids.filter(id => id !== p.id) : [...ids, p.id] });
                        }}
                        className={`w-full text-left p-2 rounded-lg border transition-colors flex items-center gap-2 ${
                          selected ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Download className="w-4 h-4 text-red-500" />
                        <p className="text-sm font-medium">{p.title}</p>
                        {selected && <span className="ml-auto text-xs text-indigo-600 font-medium">Selected</span>}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500 mt-1">No PDFs uploaded to this profile yet</p>
              )}
            </div>
            <div>
              <Label>Gating Mode</Label>
              <div className="mt-1 space-y-2">
                <button
                  onClick={() => setSettings({ ...settings, gating: 'none' })}
                  className={`w-full text-left p-2 rounded-lg border transition-colors ${
                    settings.gating !== 'lead_gate_before_download' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'
                  }`}
                >
                  <p className="text-sm font-medium">No gate - download directly</p>
                </button>
                <button
                  onClick={() => setSettings({ ...settings, gating: 'lead_gate_before_download' })}
                  className={`w-full text-left p-2 rounded-lg border transition-colors ${
                    settings.gating === 'lead_gate_before_download' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200'
                  }`}
                  data-testid="gating-lead-gate"
                >
                  <p className="text-sm font-medium">Require lead form before download</p>
                  <p className="text-xs text-slate-500">Visitor must submit contact info to unlock PDF</p>
                </button>
              </div>
            </div>
          </>
        )}

        {step.type === 'cta' && (
          <>
            <div>
              <Label>Button Label</Label>
              <Input
                className="mt-1"
                value={settings.label || ''}
                onChange={(e) => setSettings({ ...settings, label: e.target.value })}
                placeholder="Learn More"
                data-testid="cta-label-input"
              />
            </div>
            <div>
              <Label>URL</Label>
              <Input
                className="mt-1"
                type="url"
                value={settings.url || ''}
                onChange={(e) => setSettings({ ...settings, url: e.target.value })}
                placeholder="https://example.com"
                data-testid="cta-url-input"
              />
            </div>
          </>
        )}

        {step.type === 'thank_you' && (
          <div>
            <Label>Message</Label>
            <Textarea
              className="mt-1"
              value={settings.message || ''}
              onChange={(e) => setSettings({ ...settings, message: e.target.value })}
              placeholder="Thank you for visiting!"
              rows={3}
              data-testid="thank-you-message"
            />
          </div>
        )}
      </div>

      <div className="flex gap-2 mt-4">
        <Button size="sm" onClick={() => onSave(settings)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-step-settings">
          Apply
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
};

export default WorkflowBuilder;
